"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Box,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface BackchannelFrequencySliderProps {
  value: number | undefined;
  onChange: (value: number) => Promise<boolean> | void;
}

export const BackchannelFrequencySlider: React.FC<
  BackchannelFrequencySliderProps
> = ({ value = 0.8, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = (newValue: number) => {
    setLocalValue(newValue);
  };

  const handleChangeEnd = async (newValue: number) => {
    if (newValue === value) return;
    setIsUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <FormLabel
        fontSize="sm"
        fontWeight="medium"
        color={isDark ? "gray.300" : "gray.700"}
        mb={1}
      >
        Backchannel Frequency
      </FormLabel>
      <HStack spacing={4}>
        <Slider
          aria-label="Backchannel Frequency"
          min={0}
          max={1}
          step={0.01}
          value={localValue}
          onChange={handleChange}
          onChangeEnd={handleChangeEnd}
          isDisabled={isUpdating}
          flex={1}
        >
          <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
            <SliderFilledTrack bg="gray.500" />
          </SliderTrack>
          <SliderThumb boxSize={4} />
        </Slider>
        <Box minW="20px" textAlign="center">
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={isDark ? "gray.300" : "gray.700"}
          >
            {localValue.toFixed(2)}
          </Text>
        </Box>
      </HStack>
    </FormControl>
  );
};
